import { useState } from 'react';
import { useRouter } from 'next/router';
import { useSelector } from 'react-redux';
import { sendBookingEmail } from '../api/api';
import { gtmEvent } from '../services/gtmService';
import { hashEmail } from '../helpers/hashUtils';
import * as geolib from 'geolib';

export const useBookingForm = () => {
    const router = useRouter();
    const bookingData = useSelector(state => state.booking);
    
    const [errors, setErrors] = useState({});
    const [isClickedSubmit, setIsClickedSubmit] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        countryCode: '+44',
        smallSuitCases: '0',
        largeSuitCases: '0',
        reason: '',
        details: '',
        isChecked: false,
    });

    // Function to calculate distance between pickup and dropoff locations
    const calculateDistance = () => {

        if (bookingData.pickupLat && bookingData.pickupLong && bookingData.dropOffLat && bookingData.dropOffLong) {
            try {
                const distance = geolib.getDistance(
                    {
                        lat: bookingData.pickupLat,
                        lng: bookingData.pickupLong,
                    },
                    {
                        lat: bookingData.dropOffLat,
                        lng: bookingData.dropOffLong,
                    }
                );
                
                // Convert from meters to kilometers, then to miles
                const distanceInKm = geolib.convertDistance(distance, 'km');
                const distanceInMiles = distanceInKm * 0.621371;

                
                return distanceInMiles;
            } catch (error) {
                console.error('❌ Error calculating distance:', error);
                return 0;
            }
        } else {
            console.log('⚠️ Missing location data for distance calculation');
            return 0;
        }
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value,
        }));

        // Clear error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: '',
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        // First name validation
        if (!formData.firstName.trim()) {
            newErrors.firstName = 'Please enter first name';
        } else if (!/^[a-zA-Z\s]+$/.test(formData.firstName.trim())) {
            newErrors.firstName = 'First name can only contain letters and spaces';
        }

        // Last name validation
        if (!formData.lastName.trim()) {
            newErrors.lastName = 'Please enter last name';
        } else if (!/^[a-zA-Z\s]+$/.test(formData.lastName.trim())) {
            newErrors.lastName = 'Last name can only contain letters and spaces';
        }

        if (!formData.email.trim()) {
            newErrors.email = 'Please enter email address';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Please enter valid email address';
        }

        if (!formData.phone.trim()) {
            newErrors.phone = 'Please enter contact no';
        } else if (formData.phone.length < 9) {
            newErrors.phone = 'Contact No Must Have At Least 9 Digits';
        } else if (formData.phone.length > 12) {
            newErrors.phone = 'Contact No Cannot Exceed 12 Digits';
        }

        if (!formData.isChecked) {
            newErrors.isChecked = 'Please agree to the terms and conditions';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };


    

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsClickedSubmit(true);

        if (validateForm()) {
            try {
                setIsSubmitting(true);

                // Get cookie consent from localStorage
                const cookieConsent = localStorage.getItem('cookie-consent') || 'not-set';

                // Create payload according to the specified format
                // Calculate distance
                const calculatedDistance = calculateDistance();
                
                const payload = {
                    email: formData.email?.trim() || '',
                    firstName: formData.firstName || '',
                    lastName: formData.lastName || '',
                    phoneNumber: formData.phone || '',
                    phonePreFix: formData.countryCode || '+44',
                    smallSuitCases: formData.smallSuitCases || '0',
                    largeSuitCases: formData.largeSuitCases || '0',
                    reason: formData.reason || '',
                    additionalInformation: formData.details || '',
                    additionalDropoffs: formData.additionalDropoffs || [],
                    additionalPickups: formData.additionalPickups || [],

                    // Booking Data
                    departure: bookingData.departureUnix || 0,
                    departureString: (bookingData.departureUnix || 0).toString(),
                    destination: bookingData.dropOffAddress || '',
                    distance: calculatedDistance, // Now calculated using geolib
                    dropOffLat: bookingData.dropOffLat,
                    dropOffLong: bookingData.dropOffLong,

                    source: bookingData.pickUpAddress || '',
                    pickupLat: bookingData.pickupLat,
                    pickupLong: bookingData.pickupLong,

                    numberOfPassengers: parseInt(bookingData.noOfPassengers) || 1,

                    returnTime: bookingData.tripType === '2' ? bookingData.returnUnix || 0 : null,
                    returnTimeString: bookingData.tripType === '2' ? (bookingData.returnUnix || 0).toString() : null,

                    tripType: bookingData.tripType === '1' ? 0 : 1,
                    browserType: navigator.userAgent || 'Unknown',

                    googleAdId: bookingData.gclid || null,
                    leadSource: process.env.NEXT_PUBLIC_LEAD_SOURCE,
                    cookieConsent: cookieConsent,
                    formSource: bookingData.formSource || 'unknown',
                    // Additional fields for better tracking
                    timestamp: new Date().toISOString(),
                };

                // Validate payload before sending
                if (!payload.email || !payload.firstName || !payload.lastName || !payload.phoneNumber) {
                    console.error('❌ Required fields are missing in the payload');
                    throw new Error('Required fields are missing in the payload');
                }

                // Submit form data to backend
                const response = await sendBookingEmail(payload);

                // GTM Tracking - Only if GTM is enabled

                if (response.success === 1 && window.gtag && typeof window.gtag === 'function' && process.env.NEXT_PUBLIC_GTM_ID) {
                    // console.log('✅ GTM tracking enabled, proceeding...');
                    try {
                        // Hash email for privacy-compliant tracking
                        const hashedEmail = await hashEmail(payload.email);
                        
                        localStorage.setItem('FormValues', JSON.stringify(payload));
                        localStorage.setItem('JobSuccess', JSON.stringify(true));

                        // Prepare analytics data for GTM
                        if (typeof window !== "undefined" && window.gtag) {
                        const analytics = {
                            additionalInformation: payload.additionalInformation,
                            departure: payload.departure,
                            destination: payload.destination,
                            email: payload.email,
                            firstName: payload.firstName,
                            largeSuitCases: payload.largeSuitCases,
                            lastName: payload.lastName,
                            numberOfPassengers: payload.numberOfPassengers,
                            phoneNumber: payload.phoneNumber,
                            phonePreFix: payload.phonePreFix,
                            returnTime: payload.returnTime,
                            smallSuitCases: payload.smallSuitCases,
                            source: payload.source,
                            tripType: payload.tripType,
                        };


                        // Fire the Personal Information form event using existing gtmEvent
                            window.gtag('event', 'Personal_Information_form', {
                            event_category: 'Personal_Information',
                            event_label: 'Personal_Information',
                            value: analytics,
                        });

    
                        // Fire the conversion event with hashed email using gtag directly
                            window.gtag('event', 'conversion', {
                                send_to: 'AW-831601075/UNUcCNnwuuwZELPzxIwD',
                                value: 1.0,
                                currency: 'GBP',
                                user_data: {
                                    email: hashedEmail,
                                },
                            });

                        }
                    } catch (trackingError) {
                        console.error('❌ GTM tracking error:', trackingError);
                        // Don't block form submission if tracking fails
                    }
                } else {
                    console.warn('⚠️ GTM tracking skipped - conditions not met');
                }

                // Redirect to success page
                await router.push('/booking-successful');
            } catch (error) {
                console.error('❌ Form submission failed:', error);
                console.error('❌ Error details:', {
                    message: error.message,
                    stack: error.stack,
                    name: error.name
                });

                // Provide more specific error messages
                let errorMessage = 'Failed to submit form. Please try again.';
                if (error.message.includes('Required fields are missing')) {
                    errorMessage = 'Please fill in all required fields.';
                } else if (error.message.includes('Failed to send email')) {
                    errorMessage = 'Network error. Please check your connection and try again.';
                }

                alert(errorMessage);
            } finally {
                setIsSubmitting(false);
            }
        } else {
            setIsSubmitting(false);
        }
    };

    return {
        formData,
        errors,
        isClickedSubmit,
        isSubmitting,
        handleInputChange,
        validateForm,
        handleSubmit,
    };
};
